import useGetMyAccountPipelines from '@/api/queries/me/useGetMyAccountPipelines';
import DropdownField from '@payaca/components/dropdownField/DropdownField';
import { CreateRepeatProjectActionConfig } from '@payaca/types/automationTypes';
import { FC } from 'react';

const NOTHING_SELECTED_LABEL = 'Nothing selected';

export const CreateRepeatProjectActionConfigFieldset: FC<{
  config: CreateRepeatProjectActionConfig;
  onChange: (changeValue: { [key: string]: any }) => void;
  onTouch?: (fieldName: string) => void;
}> = ({ config, onChange, onTouch }) => {
  const { data: pipelinesData } = useGetMyAccountPipelines();

  // This dropdown emits publicIds, while the sibling pipeline stage fieldset
  // reads numeric ids out of the 'deal.pipelineId' condition. Both id spaces
  // are in play on this page.
  const pipelineOptions = [
    // A real entry rather than only a placeholder, so the choice can be undone.
    { label: NOTHING_SELECTED_LABEL, value: '' },
    ...(pipelinesData || []).map((pipeline) => ({
      label: pipeline.title,
      value: pipeline.publicId,
    })),
  ];

  return (
    <fieldset className="automation-action-config-fieldset">
      <p>This action will:</p>
      <ul>
        <li>
          Create a project for the same customer, at the triggering
          project&apos;s site address, tagged &quot;Repeat&quot; and
          &quot;Reminder&quot;
        </li>
        <li>
          Add an event to the new project 12 months from the moment the
          automation runs, titled &quot;Service due reminder&quot;, starting at
          12:00 in your account&apos;s timezone and lasting one hour, tagged
          &quot;Repeat&quot; and &quot;Reminder&quot;
        </li>
      </ul>
      <p>
        Nothing is created when the triggering project is less than a minute
        old, so a repeat project cannot immediately re-run the automation that
        created it.
      </p>
      <DropdownField
        label={'Which pipeline should the repeat project go in?'}
        description={
          'Leave this unselected to use the same pipeline as the triggering project.'
        }
        // Only reached if the stored publicId names no pipeline the account
        // still has; the blank option covers the ordinary unselected case.
        emptyText={NOTHING_SELECTED_LABEL}
        name={'targetPipelinePublicId'}
        value={config.targetPipelinePublicId || ''}
        options={pipelineOptions}
        onChange={onChange}
        onTouch={onTouch}
      />
    </fieldset>
  );
};
